import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import type { State } from 'types/redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  changeMode,
  loadStats,
} from '@disco/state-management/actions/StatsActions'
import { ANALYTICS_UNLIMITED } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import StatsDateRange from 'components/common/stats-date-range'
import UserName from 'components/common/user-name'
import DocumentHeadTags from 'components/document-head-tags'
import DataTable from 'components/ui/data-table'
import InfiniteScroll from 'components/ui/infinite-scroll'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'

const InsightsTeamUsersPlaylists = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    endDate,
    entities: { users },
    startDate,
  }: State['stats'] = useSelector(state => state.stats)
  const isFeatureEnabled = useFeatureEnabled(ANALYTICS_UNLIMITED)
  useEffect(() => {
    if (!isFeatureEnabled) {
      navigate('/insights/recipients')
    }
  }, [isFeatureEnabled])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const loadData = useCallback(async () => {
    await dispatch(loadStats('users', 'playlists'))
    setIsLoading(false)
  }, [dispatch])
  useEffect(() => {
    dispatch(changeMode('team'))
  }, [])
  useEffect(() => {
    if (isFeatureEnabled) {
      dispatch(sendMixpanelEvent('View more - Most playlists by team member'))
    }
  }, [isFeatureEnabled])
  useEffect(() => {
    if (isFeatureEnabled) {
      setIsLoading(true)
      loadData()
    }
  }, [endDate, isFeatureEnabled, startDate])
  const loadNext = useCallback(
    (next: string) => {
      dispatch(loadStats('users', 'playlists', true))
    },
    [dispatch],
  )
  return (
    <>
      <PageHeader.Breadcrumbs
        breadcrumbs={{
          content: 'Back',
          link: '/insights/team',
        }}
      />
      <PageContainer>
        <PageContainer.Section
          title="Most playlists created by team member"
          action={<StatsDateRange disabled={!isFeatureEnabled} />}
        >
          <DocumentHeadTags title="Most playlists created by team member | DISCO" />
          <InfiniteScroll next={users.playlists.next} loadNext={loadNext}>
            <DataTable
              appearance="plain"
              columns={[
                {
                  name: 'Name',
                  render: ({ email, first_name, last_name }) => (
                    <UserName
                      user={{
                        email,
                        first_name,
                        last_name,
                      }}
                    />
                  ),
                },
                {
                  name: 'Created',
                  width: 'content',
                  render: (item: Record<string, any>) =>
                    item.stats.playlist_versions_owned,
                },
              ]}
              data={users.playlists.data}
              loading={isLoading}
              rows={5}
            />
          </InfiniteScroll>
        </PageContainer.Section>
      </PageContainer>
    </>
  )
}

export default InsightsTeamUsersPlaylists
